import "./DailyReports.css";
import { Checkbox, NoticeBox, Tags } from "../../../ui";
import AppFileNode from "../../components/AppFileNode/AppFileNode";
import ReportFilesManager from "../../components/ReportFilesManager/ReportFilesManager";
import { fileNameOf, isVersionedReport } from "../../components/ReportFileSlot/ReportFileSlot";
import {
  APPS,
  APP_STATUS,
  DAILY_SPEC,
  MONTH_NAMES,
} from "../../utils/ludDocuments.constants";
import {
  formatDate,
  getLudFileName,
  openLudFile,
} from "../../utils/ludDocuments.helpers";

export default function DailyReports({
  restaurant,
  lang,
  token,
  refreshRestaurant,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const isL = restaurant.plan === "L";
  const periodOptions = [
    { value: "orders", label: ar ? "طلبات" : "Orders" },
    { value: "compensations", label: ar ? "تعويضات" : "Compensations" },
  ];

  return (
    <>
      <div className="lud-report-headline">
        {canManage&&restaurant.enabledApps.map((key) => {
          const appStatus = APP_STATUS[restaurant.appStatus?.[key] || "active"];
          const missing = restaurant.dailyMissing.includes(key);
          const label = !appStatus.needsFiles
            ? appStatus.label[lang]
            : isL
              ? ar
                ? "ثابت"
                : "Static"
              : missing
                ? ar
                  ? "متأخر"
                  : "Late"
                : ar
                  ? "محدّث"
                  : "Updated";
          const variant = !appStatus.needsFiles
            ? appStatus.variant
            : isL
              ? "neutral"
              : missing
                ? "warning"
                : "success";

          return (
            <button
              key={key}
              type="button"
              className="lud-report-headline__chip"
              onClick={() =>
                document
                  .getElementById(`app-${key}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              <span>{APPS[key].short[lang]}</span>
              <strong>{APPS[key].label[lang]}</strong>
              <Tags variant={variant}>{label}</Tags>
            </button>
          );
        })}
      </div>

      <NoticeBox variant="info">
        {isL
          ? ar
            ? "هذا المطعم على الباقة L — التقرير اليومي يُضاف مرة واحدة فقط عند الاشتراك، ولا يتحدث بشكل دوري."
            : "This restaurant is on Plan L. The daily report is added only once at subscription and is not updated periodically."
          : ar
            ? "الهيكل: كل تطبيق ← السنة ← الشهور ← الملفات (طلبات، وتعويضات إن وُجدت). الملف الحالي يغطي الشهر الجاري من أول يوم وحتى تاريخ اليوم، ويتحدث في مكانه يومياً دون تراكم نسخ سابقة."
            : "Structure: each app → year → months → files (orders and compensations where available). The current file covers the current month from day one through today and updates in place daily without accumulating old versions."}
      </NoticeBox>

      <ReportFilesManager
        canManage={canManage}
        onError={onError}
        restaurant={restaurant}
        lang={lang}
        token={token}
        refreshRestaurant={refreshRestaurant}
        reportType="daily"
        sharedDate
        title={ar ? "رفع التقارير اليومية" : "Upload daily reports"}
        periodOptions={periodOptions}
        renderReports={({
          reportsByApp,
          renderFileSlot,
          deleteMode,
          selectedIds,
          toggleSelected,
        }) => (
          <>
            {restaurant.enabledApps.map((key) => {
              const missing = restaurant.dailyMissing.includes(key);
              const coverage = restaurant.dailyCoverage[key];
              const appReports = (reportsByApp[key] || [])
                .slice()
                .sort(
                  (a, b) =>
                    Number(b.year) - Number(a.year) ||
                    Number(b.month) - Number(a.month),
                );

              return (
                <AppFileNode
                  key={key}
                  id={`app-${key}`}
                  title={APPS[key].label[lang]}
                  subtitle={
                    coverage
                      ? `${ar ? "مضاف من" : "Added from"} ${formatDate(coverage.from, lang)} ${ar ? "إلى" : "to"} ${formatDate(coverage.to, lang)}`
                      : ar
                        ? "لا تغطية بعد"
                        : "No coverage yet"
                  }
                  status={
                    isL
                      ? ar
                        ? "ثابت — لن يتحدث"
                        : "Static — will not update"
                      : missing
                        ? ar
                          ? "لم يتحدث اليوم"
                          : "Not updated today"
                        : ar
                          ? "محدّث اليوم"
                          : "Updated today"
                  }
                  statusVariant={
                    isL ? "neutral" : missing ? "danger" : "success"
                  }
                >
                  {appReports.length > 0
                    ? Object.entries(
                        appReports.reduce((groups, report) => {
                          const groupKey = `${report.year}-${report.month}`;
                          groups[groupKey] = [
                            ...(groups[groupKey] || []),
                            report,
                          ];
                          return groups;
                        }, {}),
                      ).map(([groupKey, groupReports]) => {
                        const [year, month] = groupKey.split("-").map(Number);
                        return (
                          <div key={groupKey}>
                            <div className="lud-report-tree-title">
                              {year} — {ar ? "السنة" : "Year"}
                            </div>
                            <div className="lud-report-tree-title">
                              {MONTH_NAMES[lang][month - 1]} {year} —{" "}
                              {ar ? "الشهر" : "Month"}
                            </div>
                            {groupReports.map((report) => {
                              const periodLabel =
                                periodOptions.find(
                                  (option) => option.value === report.period,
                                )?.label || report.period;
                              return (
                                <div
                                  className="lud-report-file lud-report-file--actual"
                                  key={report.id}
                                >
                                  {deleteMode && (
                                    <Checkbox
                                      checked={selectedIds.includes(report.id)}
                                      onChange={() => toggleSelected(report.id)}
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    />
                                  )}
                                  <div className="lud-report-file__content">
                                    <strong>
                                      {APPS[key].label[lang]} — {periodLabel}
                                    </strong>
                                    <button
                                      type="button"
                                      className="lud-file-name-link"
                                      onClick={() =>
                                        openLudFile(report.currentFile, {
                                          token,
                                          storeId: restaurant.id,
                                          kind: "report",
                                          reference: report.id,
                                          onError,
                                        })
                                      }
                                    >
                                      {isVersionedReport(report)
                                        ? fileNameOf(report)
                                        : report.currentFile
                                        ? getLudFileName({
                                            file: {
                                              ...report.currentFile,
                                              app: key,
                                              type: "daily",
                                            },
                                            kind: "report",
                                            reference: report.id,
                                            originalName:
                                              report.currentFile.originalName,
                                          })
                                        : isL
                                          ? ar
                                            ? "نسخة واحدة عند الاشتراك"
                                            : "One version at subscription"
                                          : ar
                                            ? "اكسل تراكمي"
                                            : "Cumulative Excel"}
                                    </button>
                                    {renderFileSlot(report)}
                                  </div>
                                  <div className="lud-report-file__meta">
                                    <span>
                                      {report.currentFile?.uploadedAt
                                        ? `${ar ? "رُفع بتاريخ" : "Uploaded on"} ${formatDate(report.currentFile.uploadedAt, lang)}`
                                        : isL
                                          ? ar
                                            ? "عند الاشتراك"
                                            : "At subscription"
                                          : missing
                                            ? "—"
                                            : ar
                                              ? "اليوم"
                                              : "Today"}
                                    </span>
                                    <Tags
                                      variant={
                                        isL
                                          ? "neutral"
                                          : missing
                                            ? "danger"
                                            : "success"
                                      }
                                    >
                                      {isL
                                        ? ar
                                          ? "ثابت"
                                          : "Static"
                                        : missing
                                          ? ar
                                            ? "متأخر"
                                            : "Late"
                                          : ar
                                            ? "محدّث"
                                            : "Updated"}
                                    </Tags>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    : (DAILY_SPEC[key] || []).map((file) => (
                        <div className="lud-report-file" key={file.en}>
                          <div>
                            <strong>
                              {APPS[key].label[lang]} — {file[lang]}
                            </strong>
                            <small>
                              {isL
                                ? ar
                                  ? "نسخة واحدة عند الاشتراك"
                                  : "One version at subscription"
                                : ar
                                  ? "اكسل تراكمي"
                                  : "Cumulative Excel"}
                            </small>
                          </div>
                          <div className="lud-report-file__meta">
                            <span>
                              {isL
                                ? ar
                                  ? "عند الاشتراك"
                                  : "At subscription"
                                : missing
                                  ? "—"
                                  : ar
                                    ? "اليوم"
                                    : "Today"}
                            </span>
                            <Tags
                              variant={
                                isL ? "neutral" : missing ? "danger" : "success"
                              }
                            >
                              {isL
                                ? ar
                                  ? "ثابت"
                                  : "Static"
                                : missing
                                  ? ar
                                    ? "متأخر"
                                    : "Late"
                                  : ar
                                    ? "محدّث"
                                    : "Updated"}
                            </Tags>
                          </div>
                        </div>
                      ))}
                </AppFileNode>
              );
            })}
          </>
        )}
      />
    </>
  );
}
